import { Box, IconButton, styled, Tooltip } from '@mui/material';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { CardContainer, ThemeTypography } from 'DesignSystem';
import CountryRiskLevel from 'DesignSystem/DataDisplay/CountryRiskLevel/CountryRiskLevel';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import ItemIssue from 'DesignSystem/DataDisplay/ItemIssue/ItemIssue';
import Icon, { TIconName } from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC, MouseEvent } from 'react';
import { ESiteType, IBaseSite, IFarmSite } from 'FeatureModules/Site/types/site.types';
import { getCountryRiskLevelFromCode } from 'utils/country.utils';
import {
  getSiteSecondTitle,
  getSiteTitle,
  SiteTypeLabels,
} from 'FeatureModules/Site/utils/site.utils';
import FlexBox from 'Components/FlexBox';
import { EGeoDataIssueSeverityLevel, IGeoDataIssue } from '../types/dataset.types';

interface ISiteItemProps {
  site: IBaseSite;
  issues: IGeoDataIssue[];
  hideSeverity?: boolean;
  onClick?: (site: IBaseSite) => void;
  onEdit?: (site: IBaseSite) => void;
  onEditPlots?: (farm: IFarmSite) => void;
  onDelete?: (site: IBaseSite) => void;
  onManage?: (site: IBaseSite) => void;
}

const SiteItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  maxWidth: '100%',

  '&:not(:last-child)': {
    marginBottom: theme.spacing(1),
  },
}));

const TitleContainer = styled(FlexBox)(({ theme }) => ({
  wordBreak: 'break-all',
}));

const SiteItem: FC<ISiteItemProps> = ({
  site,
  issues,
  hideSeverity,
  onEdit,
  onClick,
  onEditPlots,
  onDelete,
  onManage,
}) => {
  const { company } = useCompanyPlanContext();
  const warnings = issues?.filter(
    issue => issue.severityLevel === EGeoDataIssueSeverityLevel.WARNING
  );

  const errors = issues?.filter(issue => issue.severityLevel === EGeoDataIssueSeverityLevel.ERROR);

  const rawMaterialTitle = site.siteType === ESiteType.CLUSTER && site.sitesRawMaterial?.title;

  const subTitleTags: { icon: TIconName; text: string; key: string }[] = [
    {
      key: 'siteType',
      icon: 'tag-filled' as TIconName,
      text:
        site.siteType === ESiteType.CLUSTER && site.sitesType
          ? `${SiteTypeLabels[site.sitesType]} cluster`
          : SiteTypeLabels[site.siteType],
    },
    ...(site.siteType === ESiteType.CLUSTER
      ? [
          {
            key: 'farms',
            icon: 'farm' as TIconName,
            text: `${site.sitesCount} farm${site.sitesCount > 1 ? 's' : ''}`,
          },
        ]
      : []),
    ...(rawMaterialTitle
      ? [
          {
            key: 'rawMaterial',
            icon: 'raw-material' as TIconName,
            text: rawMaterialTitle,
          },
        ]
      : []),
    {
      key: 'location',
      icon: 'location' as TIconName,
      text: site.locationName,
    },
  ].filter(Boolean);

  const handleEditPlots = (event: MouseEvent) => {
    event.stopPropagation();
    onEditPlots?.(site as IFarmSite);
  };
  const handleEdit = (event: MouseEvent) => {
    event.stopPropagation();
    onEdit?.(site);
  };
  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    onDelete?.(site);
  };
  const handleManage = (event: MouseEvent) => {
    event.stopPropagation();
    onManage?.(site);
  };

  const countryRiskLevel = getCountryRiskLevelFromCode(site.countryCode);
  const siteSecondTitle = getSiteSecondTitle({
    site,
    farmDisplayOption: company.farmDisplayOption,
    siteDisplayOption: company.siteDisplayOption,
  });

  return (
    <SiteItemContainer key={site.id} display="flex" alignItems="center" gap={1}>
      {!hideSeverity && (
        <FlexBox>
          {!errors.length && !warnings.length && (
            <Icon name="check-filled" size="large" color="green" />
          )}
          {!!errors.length && <Icon name="error-filled" size="large" color="red" />}
          {!!warnings.length && !errors.length && (
            <Icon name="info-filled" size="large" color="yellow" />
          )}
        </FlexBox>
      )}
      <CardContainer padding={1.5} blueHover={!!onClick} onClick={() => onClick?.(site)}>
        <FlexBox maxWidth="100%" justifyContent="space-between">
          {/*  120px for the action button container & 24px padding */}
          <FlexBox width="calc(100% - 120px - 24px)">
            {!!site.image ? (
              <Image width={44} height={44} image={site.image} />
            ) : (
              <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
                <Icon name="site-placeholder" size="xx-large" color="blue-ice" />
              </Box>
            )}

            {/* 12px margin left & 44px image */}
            <Box width="calc(100% - 56px)" ml={2}>
              <Box maxWidth="100%" display="flex" alignItems="center" gap={0.5}>
                <TitleContainer gap={1} maxWidth="50%">
                  <ThemeTypography variant="ITEM_TITLE" autoOverflow>
                    {getSiteTitle({
                      site,
                      farmDisplayOption: company.farmDisplayOption,
                      siteDisplayOption: company.siteDisplayOption,
                    })}
                  </ThemeTypography>
                  {siteSecondTitle && (
                    <>
                      <ThemeTypography variant="BODY_MEDIUM" color="GRAY_100">
                        |
                      </ThemeTypography>
                      <ThemeTypography autoOverflow variant="BODY_MEDIUM" color="GRAY_100">
                        {siteSecondTitle}
                      </ThemeTypography>
                    </>
                  )}
                </TitleContainer>

                {countryRiskLevel && (
                  <Box ml={1}>
                    <CountryRiskLevel riskLevel={countryRiskLevel} />
                  </Box>
                )}

                {!!errors.length && <ItemIssue issues={errors} severity="error" itemName="Site" />}

                {!!warnings.length && (
                  <ItemIssue issues={warnings} severity="warning" itemName="Site" />
                )}
              </Box>

              <FlexBox maxWidth="100%" gap={1}>
                {subTitleTags.map(item => (
                  <FlexBox key={item.key} gap={0.5} flex="0 1 auto" minWidth={50}>
                    <Icon name={item.icon} size="small" color="gray-80" />
                    <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                      {item.text}
                    </ThemeTypography>
                  </FlexBox>
                ))}
              </FlexBox>
            </Box>
          </FlexBox>

          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mr={1}>
            {!!onManage && (
              <Tooltip
                title={
                  site.siteType === ESiteType.CLUSTER
                    ? 'Manage site cluster'
                    : site.siteClusterId
                    ? 'Change site cluster'
                    : 'Add site to cluster'
                }
                placement="top"
              >
                <IconButton onClick={handleManage}>
                  <Icon name="site-cluster-manage" color="gray-100" />
                </IconButton>
              </Tooltip>
            )}
            {!!onEditPlots && site.siteType === ESiteType.FARM && (
              <Tooltip title="Edit cultivation area">
                <IconButton onClick={handleEditPlots}>
                  <Icon name="polygon-mapping" size="medium" />
                </IconButton>
              </Tooltip>
            )}
            {!!onEdit && (
              <Tooltip title="Edit site">
                <IconButton onClick={handleEdit}>
                  <Icon name="edit" size="medium" />
                </IconButton>
              </Tooltip>
            )}
            {!!onDelete && (
              <Tooltip title="Delete site">
                <IconButton onClick={handleDelete}>
                  <Icon name="delete" size="medium" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </FlexBox>
      </CardContainer>
    </SiteItemContainer>
  );
};

export default SiteItem;
